import { useCallback, useMemo, useState } from 'react'
import {
  Autocomplete,
  IconButton,
  InputBase,
  Popper,
  PopperProps,
  styled,
} from '@mui/material'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { colorPalette } from '../../../../style/colorPalette'
import { SprintOption } from '../../hooks/sprintOptions'
import { SprintStatus } from '../../../../../lib/functions/sprint'
import { getSprintStatusColor } from '../../../../../lib/functions/sprintStatusColor'

type Props = {
  value?: string
  options: SprintOption[]
  onChange: (value: string | undefined) => void
  placeholder?: string
  disabled?: boolean
}

// FINISHED と CANCELED は終わったスプリントとしてまとめて確認することが多いため、
// 1つの折りたたみグループに統合する。INPROGRESS / STANDBY はそれぞれ独立したグループ。
const FINISHED_AND_CANCELED_GROUP = 'FINISHED & CANCELED'

// オプション行の列幅。日付列は固定幅、名前列は最長の名前に合わせた可変幅にする。
const DATE_COLUMN_WIDTH = '64px'
// 名前列の幅は ch 単位で算出するが、プロポーショナルフォントでは ch と実幅にズレがあるため
// 末尾が切れないよう少し余白を足す。
const NAME_WIDTH_BUFFER_CH = 1

const getSprintStatusGroup = (status: SprintStatus): string =>
  status === SprintStatus.FINISHED || status === SprintStatus.CANCELED
    ? FINISHED_AND_CANCELED_GROUP
    : status

// PopperComponent はインライン関数で渡すと再描画のたびに別コンポーネント扱いになり、
// ポップアップ（リストボックス）が再マウントされてスクロール位置が先頭に戻ってしまう。
// 安定した参照にすることで再マウントを防ぎ、グループ開閉時もスクロール位置を保つ。
const BottomStartPopper = (props: PopperProps) => (
  <Popper {...props} placement="bottom-start" />
)

// SprintReportNew ヘッダの「開始/終了スプリント」セレクタ。
// ConditionRow の DateBox / GroupKeyBox と同じ薄ボーダー + 白背景に揃え、
// プルダウンはステータス (INPROGRESS → STANDBY → FINISHED & CANCELED) でグループ表示し、
// 各グループは見出しクリックで折りたためる（FINISHED & CANCELED は初期状態で閉じる）。
export const SprintSelect = ({
  value,
  options,
  onChange,
  placeholder,
  disabled,
}: Props) => {
  const [inputValue, setInputValue] = useState('')
  // FINISHED & CANCELED グループだけ初期状態で閉じておく（過去スプリントは普段見ないため）。
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({ [FINISHED_AND_CANCELED_GROUP]: true })

  const toggleGroup = useCallback((group: string) => {
    setCollapsedGroups(prev => ({ ...prev, [group]: !prev[group] }))
  }, [])

  const selectedOption = useMemo(
    () => options.find(o => o.value === value) ?? null,
    [options, value]
  )

  // 名前列の幅を一番長い名前に合わせて固定する。これで名前を省略せずフル表示しつつ、
  // 全行で名前列の幅が揃うので日付・ステータスの縦位置が揃う。
  const nameColumnWidth = useMemo(() => {
    const maxLength = options.reduce(
      (max, o) => Math.max(max, o.name.length),
      0
    )
    return `${maxLength + NAME_WIDTH_BUFFER_CH}ch`
  }, [options])

  // 文字入力で絞り込み中は、折りたたまれたグループ内の一致候補が隠れて
  // 「検索したのに出てこない」状態になるのを避けるため、全グループを開いて扱う。
  // 選択済みの値がそのまま入力欄に入っているだけ（=絞り込みではない）の場合は対象外。
  const isSearching =
    inputValue.trim().length > 0 && inputValue !== selectedOption?.name

  const handleChange = useCallback(
    (_: unknown, option: SprintOption | null) => {
      onChange(option?.value)
    },
    [onChange]
  )

  const handleInputChange = useCallback((_: unknown, newValue: string) => {
    setInputValue(newValue)
  }, [])

  const clear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onChange(undefined)
      setInputValue('')
    },
    [onChange]
  )

  return (
    <StyledAutocomplete
      value={selectedOption}
      inputValue={inputValue}
      options={options}
      onChange={handleChange}
      onInputChange={handleInputChange}
      disabled={disabled}
      getOptionLabel={option => option.name}
      groupBy={option => getSprintStatusGroup(option.status)}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      renderInput={params => (
        // DateBox と構造を揃えるため、Autocomplete の renderInput を TextField ベースではなく
        // <InputBase /> 直接にする。これで TextField/FormControl 経由の余計な padding 上書きを避け、
        // 横余白は StyledAutocomplete 側の wrapper padding (DateBox と同じ '0 5px') だけで効くようにする。
        <InputBase
          ref={params.InputProps.ref}
          className={params.InputProps.className}
          inputProps={params.inputProps}
          startAdornment={params.InputProps.startAdornment}
          endAdornment={
            value ? (
              <ClearButton onClick={clear} size="small">
                <ClearRoundedIcon sx={{ fontSize: '14px' }} />
              </ClearButton>
            ) : (
              params.InputProps.endAdornment
            )
          }
          placeholder={placeholder}
          disabled={params.disabled}
          fullWidth={true}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.value}>
          <OptionContent nameColumnWidth={nameColumnWidth}>
            <OptionName>{option.name}</OptionName>
            {option.subText && <SubText>{option.subText}</SubText>}
            {/* 統合グループ (FINISHED & CANCELED) では中身が混在するため、
                どちらのステータスかを各項目に表示して区別できるようにする。
                日付の右にステータスを置く。 */}
            {(option.status === SprintStatus.FINISHED ||
              option.status === SprintStatus.CANCELED) && (
              <StatusLabel chipColor={getSprintStatusColor(option.status)}>
                {option.status}
              </StatusLabel>
            )}
          </OptionContent>
        </li>
      )}
      renderGroup={params => {
        const isCollapsed =
          !isSearching && (collapsedGroups[params.group] ?? false)
        return (
          <GroupContainer key={params.key}>
            <GroupHeader
              // mousedown のデフォルト挙動を止めて input のフォーカスを保持し、
              // ヘッダクリックでポップアップが閉じないようにする。
              onMouseDown={e => e.preventDefault()}
              onClick={() => toggleGroup(params.group)}
            >
              <GroupChevron expanded={!isCollapsed} />
              <span>{params.group}</span>
            </GroupHeader>
            {!isCollapsed && <GroupItems>{params.children}</GroupItems>}
          </GroupContainer>
        )
      }}
      PopperComponent={BottomStartPopper}
      slotProps={{
        paper: {
          sx: {
            '& .MuiAutocomplete-listbox': {
              paddingTop: 0,
              fontSize: '12px',
            },
          },
        },
      }}
      noOptionsText=""
      size="small"
    />
  )
}

// DateBox と同じ薄ボーダー + 白背景 + 横 padding にして見た目を統一する。
// renderInput で <InputBase /> を直接渡しているので、Autocomplete 由来の特殊な padding 上書きは不要。
const StyledAutocomplete = styled(Autocomplete<SprintOption>)({
  width: '120px',
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  padding: '0 5px',
  '& .MuiInputBase-root': {
    minHeight: 'unset',
    fontSize: '11px',
  },
  '& .MuiInputBase-input': {
    padding: '0 !important',
    height: '20px',
    lineHeight: '20px',
  },
  // Autocomplete のデフォルト dropdown / clear ボタン (endAdornment) を縦中央に小さく収める。
  '& .MuiAutocomplete-endAdornment': {
    top: '50%',
    transform: 'translateY(-50%)',
  },
  '& .MuiAutocomplete-endAdornment .MuiIconButton-root': {
    padding: '2px',
  },
})

const ClearButton = styled(IconButton)({
  padding: '2px',
})

const GroupContainer = styled('li')({
  listStyle: 'none',
  padding: 0,
})

// 旧 .MuiAutocomplete-groupLabel と同じ見た目（太字・薄グレー背景・sticky）に、
// 折りたたみ用の chevron とクリック領域を足したヘッダ。
const GroupHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  cursor: 'pointer',
  userSelect: 'none',
  fontWeight: 'bold',
  fontSize: '11px',
  lineHeight: '1.2',
  padding: '8px 8px 4px',
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: colorPalette.monotone[1],
})

const GroupChevron = styled(ExpandMoreRoundedIcon, {
  shouldForwardProp: prop => prop !== 'expanded',
})<{ expanded: boolean }>(({ expanded }) => ({
  fontSize: '16px',
  color: colorPalette.monotone[5],
  transition: 'transform 0.15s',
  transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
}))

const GroupItems = styled('ul')({
  margin: 0,
  padding: 0,
})

// 名前 / 日付 / ステータス を固定幅の列に分け、名前の長さに関わらず日付とステータスの
// 縦位置が揃うようにする。名前列は一番長い名前に合わせた可変固定幅 (nameColumnWidth)。
const OptionContent = styled('div', {
  shouldForwardProp: prop => prop !== 'nameColumnWidth',
})<{ nameColumnWidth: string }>(({ nameColumnWidth }) => ({
  display: 'grid',
  gridTemplateColumns: `${nameColumnWidth} ${DATE_COLUMN_WIDTH} auto`,
  alignItems: 'center',
  gap: '8px',
  width: '100%',
}))

const OptionName = styled('span')({
  fontSize: '12px',
  whiteSpace: 'nowrap',
})

// FINISHED / CANCELED の項目だけに付くステータスチップ。WBS ステータス色 (公式) を
// 背景に敷いた塗りつぶし型で、一目でどちらのステータスか区別できるようにする。
const StatusLabel = styled('span', {
  shouldForwardProp: prop => prop !== 'chipColor',
})<{ chipColor: string }>(({ chipColor }) => ({
  justifySelf: 'start',
  fontSize: '9px',
  fontWeight: 'bold',
  lineHeight: '1.4',
  padding: '0 4px',
  borderRadius: '3px',
  whiteSpace: 'nowrap',
  color: colorPalette.monotone[10],
  backgroundColor: chipColor,
}))

const SubText = styled('span')({
  fontSize: '10px',
  color: colorPalette.monotone[5],
  textAlign: 'right',
  whiteSpace: 'nowrap',
})
